import { ssoClientInvoke } from '@kaltura-ott/tvpil';

import { RhinoError } from '../../error';
import { loginAnonymous } from '../../phoenix/services/ottUser';
import { getDeviceConfiguration } from '../configuration/getDeviceConfiguration';
import { createMissingInitRhinoError } from '../errors';

type FetchEboxTokenArgs = {
  brandId: number;
  householdId: string;
  partnerId: number;
  udid: string;
};

type FetchTokenArgs = FetchEboxTokenArgs & {
  deviceFamilyId: number;
  ks?: string;
};

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/XTVEXT/pages/2133886941/O2CZ+SSO+Adapter+invoke#Kaltura-ssoAdapterProfile.invoke-(%22intent%22%3A-%22eBOX-Token%22)
 */
export async function fetchEboxToken({ brandId, householdId, partnerId, udid }: FetchEboxTokenArgs) {
  const configuration = getDeviceConfiguration();

  if (!configuration) {
    throw createMissingInitRhinoError();
  }

  try {
    const token = await fetchToken({
      brandId,
      deviceFamilyId: configuration.deviceFamilyId,
      householdId,
      partnerId,
      udid,
    });

    return token;
  } catch (error) {
    if (error instanceof Error && !hasKsExpired(error)) {
      throw new RhinoError('NETWORK_API_ERROR', undefined, {
        nativeError: error,
      });
    }
  }

  const { ks } = await loginAnonymous({
    partnerId,
  });

  try {
    return fetchToken({
      brandId,
      deviceFamilyId: configuration.deviceFamilyId,
      householdId,
      ks,
      partnerId,
      udid,
    });
  } catch (error) {
    throw new RhinoError('NETWORK_API_ERROR', undefined, {
      nativeError: error,
    });
  }
}

async function fetchToken({ brandId, deviceFamilyId, householdId, ks, partnerId, udid }: FetchTokenArgs) {
  const { adapterData } = await ssoClientInvoke(
    partnerId,
    {
      adapterData: [
        {
          key: 'externalHHId',
          value: householdId,
        },
        {
          key: 'deviceFamilyId',
          value: String(deviceFamilyId),
        },
        {
          key: 'deviceBrandId',
          value: String(brandId),
        },
        {
          key: 'deviceUDID',
          value: udid,
        },
      ],
      intent: 'eBOX Token',
    },
    ks,
  );

  return adapterData.autoLoginPin;
}

function hasKsExpired(error: Error) {
  if (!('response' in error && error.response)) {
    return false;
  }

  const { status, data } = error.response as { status: number; data: { result: string } };

  return status === 401 && data.result.toLowerCase().includes('could not authenticate ks');
}
